import React from "react";
import ReactDOM from 'react-dom';
import { Button } from "../Button";
import { showErrorToast, showSuccessToast } from "../../lib/ToastUtility";

type WebLinkModalProps = {
  open: boolean;
  title: string;
  webLink: string;
  kioskSecret: string;
  impactShareUrl?: string;
  onClose: () => void;
};

const WebLinkModal: React.FC<WebLinkModalProps> = ({
  open,
  title,
  webLink,
  kioskSecret,
  impactShareUrl,
  onClose,
}) => {
  if (!open) {
    return null;
  }

  const handleCopy = async (value: string, type: "link" | "impact" | "secret") => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      if (type === "link") {
        showSuccessToast("Kiosk donation link copied to clipboard");
      } else if (type === "impact") {
        showSuccessToast("Live Impact link copied to clipboard");
      } else {
        showSuccessToast("Kiosk secret copied to clipboard");
      }
    } catch {
      showErrorToast("Could not copy. Please copy it manually.");
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-2xl border border-white/50 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-slate-900"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-slate-800 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        {/* 1. Kiosk Donation Link */}
        <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 dark:border-emerald-900/30 dark:bg-emerald-950/20">
          <div className="flex items-center justify-between">
            <label className="text-sm font-bold text-emerald-950 dark:text-emerald-200">
              1. Kiosk Donation Link (Direct Donor Checkout)
            </label>
            <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300">
              Donation Flow
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Share this link to let donors directly choose meal packages and donate via credit/debit card.
          </p>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={webLink}
              readOnly
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-xs text-gray-800 focus:outline-none dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                void handleCopy(webLink, "link");
              }}
            >
              Copy Link
            </Button>
          </div>
        </div>

        {/* 2. Live Impact Link */}
        {impactShareUrl && (
          <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50/40 p-4 dark:border-blue-900/30 dark:bg-blue-950/20">
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold text-blue-950 dark:text-blue-200">
                2. Live Impact Share Link (Social Media)
              </label>
              <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/60 dark:text-blue-300">
                Live Counts
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Shows real-time meals provided and raised amounts when shared on WhatsApp, Facebook, or Instagram.
            </p>
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={impactShareUrl}
                readOnly
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-xs text-gray-800 focus:outline-none dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  void handleCopy(impactShareUrl, "impact");
                }}
              >
                Copy Link
              </Button>
            </div>
          </div>
        )}

        {/* Kiosk Secret */}
        <div className="mt-4">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Kiosk Secret (Internal Credential)
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={kioskSecret}
              readOnly
              className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700 focus:outline-none dark:border-white/10 dark:bg-slate-800 dark:text-gray-300"
            />
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => {
                void handleCopy(kioskSecret, "secret");
              }}
            >
              Copy Secret
            </Button>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WebLinkModal;
